// sanitizeSource.ts
type Chapter = { chapterId: string; }
type Book = { id: string; chapters: Chapter[]; }
type Publisher = { id: string; books: Book[]; }
type Sub = { id: string; publishers: Publisher[]; }
type Subject = { id: string; subSubject: Sub[]; }

const PREFIX_MAP = {
  // 'source-', not 'subject-': that is the prefix generateId actually uses for
  // a newly added subject. The mismatch meant a new subject was posted with its
  // client-side id still attached instead of a blank one, and only survived
  // because the API's ObjectId deserializer quietly nulls unparseable ids.
  subject: 'source-',
  subSubject: 'subsubject-',
  publisher: 'publisher-',
  book: 'book-',
  chapter: 'chapter-',
} as const;

function hasPrefix(id: string | undefined, prefix: string) {
  return typeof id === 'string' && id.startsWith(prefix)
}

/**
 * Recursively walks a Subject tree, blanking the ids of nodes that were created
 * in the browser so the API inserts them rather than trying to look them up.
 * Server-assigned ids are left alone, and children are always kept.
 */
export function sanitizeSource(subject: Subject): Subject {
  return {
    ...subject,
    id: hasPrefix(subject.id, PREFIX_MAP.subject) ? '' : subject.id,

    subSubject: subject.subSubject?.map(sub => ({
      ...sub,
      id: hasPrefix(sub.id, PREFIX_MAP.subSubject) ? '' : sub.id,

      publishers: sub.publishers?.map(pub => ({
        ...pub,
        id: hasPrefix(pub.id, PREFIX_MAP.publisher) ? '' : pub.id,

        books: pub.books?.map(book => ({
          ...book,
          id: hasPrefix(book.id, PREFIX_MAP.book) ? '' : book.id,

          chapters: book.chapters?.map(chapter => ({
            ...chapter,
            chapterId: hasPrefix(chapter.chapterId, PREFIX_MAP.chapter)
              ? ''
              : chapter.chapterId,
          })) || [],
        })) || [],
      })) || [],
    })) || [],
  }
}
